import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Printer } from 'lucide-react';
import type { KitTemplate } from '@hcc/types';
import { useCurrentUser } from '../../hooks/useAuth';
import { usePeopleList, type PersonApi } from '../../hooks/usePeople';
import { useKitTemplatesList } from '../../hooks/useKitTemplates';
import { Button } from '../../components/ui/Button';

const ALLOWED = ['admin', 'super_admin', 'coordinator', 'hr'];

/**
 * Printable paper fallback (CLAUDE.md 6.5: "the night before, the app
 * can generate a printable attendee list and kit sheet. If the app
 * breaks entirely for any reason, the day continues on paper").
 *
 * One page per attendee in print: name + position + email at the top,
 * a kit checklist (from their position's template) below for ticking
 * by hand, and signature/date lines at the bottom. The browser's
 * built-in print dialog is the export mechanism — no PDF backend is
 * needed for this fallback path.
 */
export function PrintableList() {
  const { data: user, isLoading } = useCurrentUser();
  const peopleQ = usePeopleList('');
  const templatesQ = useKitTemplatesList();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (peopleQ.data && templatesQ.data) setReady(true);
  }, [peopleQ.data, templatesQ.data]);

  if (isLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Please <Link to="/login" className="underline">sign in</Link>.
      </main>
    );
  }
  if (!user.roles.some((r) => ALLOWED.includes(r))) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Printable paper fallback is restricted to admin / coordinator / HR.
      </main>
    );
  }

  const templateByPosition = new Map<string, KitTemplate>();
  for (const t of templatesQ.data ?? []) templateByPosition.set(t.position, t);
  const people = peopleQ.data ?? [];

  return (
    <main className="min-h-screen bg-bg text-fg print:bg-white print:p-0">
      <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8 print:max-w-none print:p-0">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3 print:hidden">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-accent/10 text-accent">
              <Printer className="h-6 w-6" strokeWidth={2} />
            </div>
            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-accent">
                Admin
              </div>
              <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
                Printable paper fallback
              </h1>
              <p className="mt-1 text-sm text-fg-muted">
                One A4 page per attendee. Use your browser's print dialog
                (Ctrl/Cmd-P). Hand-tick on the day if the app is unavailable;
                back-fill events afterwards.
              </p>
            </div>
          </div>
          <Button
            variant="primary"
            size="md"
            iconLeft={<Printer className="h-4 w-4" />}
            onClick={() => window.print()}
          >
            Print
          </Button>
        </div>

        {!ready ? (
          <p className="text-sm text-fg-muted print:hidden">Loading attendees...</p>
        ) : people.length === 0 ? (
          <p className="text-sm text-fg-muted print:hidden">
            No attendees yet. Import some via /admin/import-attendees first.
          </p>
        ) : (
          <div className="space-y-8 print:space-y-0">
            {people.map((p) => (
              <PersonSheet
                key={p.id}
                person={p}
                template={p.position ? (templateByPosition.get(p.position) ?? null) : null}
              />
            ))}
          </div>
        )}
      </div>
      <style>{`
        @media print {
          .page-break { page-break-after: always; }
          @page { size: A4; margin: 18mm; }
          body { background: white !important; }
        }
      `}</style>
    </main>
  );
}

function PersonSheet({
  person,
  template,
}: {
  person: PersonApi;
  template: KitTemplate | null;
}) {
  const items = template?.items ?? [];
  type Item = (typeof items)[number];
  const byStation = items.reduce<Record<string, Item[]>>((acc, it) => {
    (acc[it.station] = acc[it.station] ?? []).push(it);
    return acc;
  }, {});
  return (
    <article className="page-break rounded-token-lg border border-line/10 bg-surface p-6 shadow-token-sm print:rounded-none print:border-0 print:bg-white print:p-0 print:shadow-none">
      <header className="border-b border-line-strong/20 pb-3">
        <h2 className="text-2xl font-semibold">{person.full_name}</h2>
        <div className="mt-1 flex flex-wrap gap-x-6 gap-y-1 text-sm text-fg-muted">
          <span>
            <strong>Position:</strong> {person.position ?? '—'}
          </span>
          <span>
            <strong>Email:</strong> {person.email}
          </span>
          <span>
            <strong>Contract:</strong> {person.contract ?? '—'}
          </span>
        </div>
      </header>

      <section className="mt-4">
        <h3 className="text-sm font-semibold text-fg-muted">Arrival</h3>
        <p className="mt-1 text-sm">
          Arrived: <span className="inline-block min-w-[8rem] border-b border-slate-400">&nbsp;</span>
          &nbsp;&nbsp; Time:{' '}
          <span className="inline-block min-w-[6rem] border-b border-slate-400">&nbsp;</span>
          &nbsp;&nbsp; Reception staff:{' '}
          <span className="inline-block min-w-[10rem] border-b border-slate-400">&nbsp;</span>
        </p>
      </section>

      <section className="mt-4">
        <h3 className="text-sm font-semibold text-fg-muted">Kit checklist</h3>
        {items.length === 0 ? (
          <p className="mt-1 text-sm text-fg-muted">
            No template for "{person.position ?? 'unknown position'}".
          </p>
        ) : (
          <div className="mt-2 space-y-3">
            {Object.entries(byStation).map(([station, list]) => (
              <div key={station}>
                <div className="text-xs font-semibold uppercase tracking-wider text-fg-muted">
                  {station}
                </div>
                <ul className="mt-1 space-y-1 text-sm">
                  {list.map((it, idx) => (
                    <li key={`${station}-${idx}`} className="flex items-baseline gap-2">
                      <span className="inline-block h-3 w-3 border border-slate-500" />
                      <span>
                        {it.name}{' '}
                        {it.quantity > 1 ? <em className="text-fg-muted">× {it.quantity}</em> : null}
                        {it.size_required ? <em className="text-fg-muted"> (size: ____)</em> : null}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mt-6 grid grid-cols-2 gap-6 text-sm">
        <div>
          <div className="border-b border-slate-400 pb-1">&nbsp;</div>
          <div className="mt-1 text-xs text-fg-muted">Inductee signature</div>
        </div>
        <div>
          <div className="border-b border-slate-400 pb-1">&nbsp;</div>
          <div className="mt-1 text-xs text-fg-muted">Date</div>
        </div>
      </section>

      <section className="mt-4 text-xs text-fg-muted">
        <strong>Note:</strong> back-fill these issuances into the app
        once it is available again. The events table is the source of
        truth; this page is paper-only fallback.
      </section>
    </article>
  );
}
